import { useEffect, useState, useCallback, useRef } from "react";
import { api, apiError } from "@/pages/rc/rcApi";
import RCLayout from "@/pages/rc/RCLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Building2, Upload, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  ragione_sociale: "", forma_giuridica: "", codice_fiscale: "", partita_iva: "",
  indirizzo: "", sede_legale: "", referente: "", email: "", telefono: "", tipologia_ente: "",
  attivita_principale: "", note: "",
};

export default function RCOrganizations() {
  const [orgs, setOrgs] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const load = useCallback((q = "") => {
    const url = q ? `/organizations?q=${encodeURIComponent(q)}` : "/organizations";
    return api.get(url).then((r) => setOrgs(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (o) => {
    setEditing(o);
    setForm({
      ragione_sociale: o.ragione_sociale || "", forma_giuridica: o.forma_giuridica || "",
      codice_fiscale: o.codice_fiscale || "", partita_iva: o.partita_iva || "",
      indirizzo: o.indirizzo || "", sede_legale: o.sede_legale || "", referente: o.referente || "",
      email: o.email || "", telefono: o.telefono || "", tipologia_ente: o.tipologia_ente || "",
      attivita_principale: o.attivita_principale || "", note: o.note || "",
    });
    setOpen(true);
  };

  const remove = async (o) => {
    if (!window.confirm(`Eliminare definitivamente l'ente "${o.ragione_sociale}"?`)) return;
    try {
      await api.delete(`/organizations/${o.id}`);
      toast.success("Ente eliminato.");
      load(search.trim());
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/organizations/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import completato: ${data.created} creati, ${data.updated} aggiornati, ${data.skipped} ignorati.`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Errore durante l'import");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!form.ragione_sociale) { toast.error("Ragione sociale obbligatoria."); return; }
    try {
      if (editing) {
        await api.put(`/organizations/${editing.id}`, { ...form, attivita_secondarie: [] });
        toast.success("Ente aggiornato.");
      } else {
        await api.post("/organizations", { ...form, attivita_secondarie: [] });
        toast.success("Ente creato.");
      }
      setOpen(false); setForm(EMPTY); setEditing(null); load(search.trim());
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <RCLayout>
      <div data-testid="orgs-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight text-stone-900">Anagrafica Enti</h1>
            <p className="text-stone-500 text-sm mt-1">Enti e clienti del Terzo Settore.</p>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".csv" onChange={onImport} className="hidden" data-testid="import-file-input" />
            <Button data-testid="import-org-btn" variant="outline" disabled={importing}
              onClick={() => fileRef.current?.click()} className="rounded-sm">
              <Upload className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> {importing ? "Import…" : "Importa da Assieasy"}
            </Button>
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
              <DialogTrigger asChild>
                <Button data-testid="new-org-btn" onClick={openNew} className="rounded-sm bg-stone-900 hover:bg-stone-800">
                  <Plus className="w-4 h-4 mr-1.5" strokeWidth={2} /> Nuovo Ente
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>{editing ? "Modifica ente" : "Nuovo ente"}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
                  {[
                    ["ragione_sociale", "Ragione sociale *", 2],
                    ["forma_giuridica", "Forma giuridica", 1],
                    ["tipologia_ente", "Tipologia ente", 1],
                    ["codice_fiscale", "Codice fiscale", 1],
                    ["partita_iva", "Partita IVA", 1],
                    ["indirizzo", "Indirizzo", 2],
                    ["sede_legale", "Indirizzo sede legale", 2],
                    ["referente", "Referente", 1],
                    ["telefono", "Telefono", 1],
                    ["email", "Email", 2],
                    ["attivita_principale", "Attività principale", 2],
                  ].map(([k, label, span]) => (
                    <div key={k} className={span === 2 ? "col-span-2" : ""}>
                      <Label className="text-xs uppercase tracking-widest text-stone-500">{label}</Label>
                      <Input data-testid={`org-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} className="mt-1.5 rounded-sm" />
                    </div>
                  ))}
                  <div className="col-span-2">
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Note assuntive</Label>
                    <Textarea data-testid="org-note" value={form.note} onChange={(e) => set("note", e.target.value)} className="mt-1.5 rounded-sm" rows={2} />
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="save-org-btn" onClick={save} className="rounded-sm bg-stone-900 hover:bg-stone-800">Salva ente</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="relative mb-5 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" strokeWidth={1.5} />
          <Input
            data-testid="org-search-input"
            placeholder="Cerca per ragione sociale, P.IVA o codice fiscale…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-sm"
          />
        </div>

        <p className="text-xs text-stone-400 mb-3" data-testid="org-count">{orgs.length} enti</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {orgs.map((o) => (
            <div key={o.id} data-testid={`org-card-${o.id}`} className="group bg-white border border-stone-200 rounded-sm shadow-sm p-5 relative">
              <div className="absolute top-3 right-3 flex items-center gap-1">
                <Button data-testid={`edit-org-${o.id}`} variant="ghost" size="icon"
                  onClick={() => openEdit(o)} className="h-7 w-7 rounded-sm text-stone-500 hover:text-stone-900 hover:bg-stone-100">
                  <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                </Button>
                <Button data-testid={`delete-org-${o.id}`} variant="ghost" size="icon"
                  onClick={() => remove(o)} className="h-7 w-7 rounded-sm text-stone-400 hover:text-red-600 hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </Button>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-sm bg-stone-100 flex items-center justify-center shrink-0">
                  <Building2 className="w-4 h-4 text-stone-500" strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-stone-900 truncate pr-12">{o.ragione_sociale}</div>
                  <div className="text-xs text-stone-400">{o.forma_giuridica || "—"} · {o.tipologia_ente || "—"}</div>
                  {o.indirizzo && <div className="text-xs text-stone-500 mt-2 truncate">{o.indirizzo}</div>}
                  <div className="text-xs text-stone-500 mt-1">{o.email || "Nessuna email"}</div>
                  {o.telefono && <div className="text-xs text-stone-500 font-mono">{o.telefono}</div>}
                  <div className="text-xs text-stone-400 font-mono mt-1">{o.partita_iva || o.codice_fiscale || ""}</div>
                </div>
              </div>
            </div>
          ))}
          {orgs.length === 0 && <p className="text-stone-400 text-sm">Nessun ente registrato.</p>}
        </div>
      </div>
    </RCLayout>
  );
}
